import Group from 'components/utils/Group'
import Image from 'components/Common/Image/Image'
import { rem } from 'polished'
import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { dateDifference } from 'lib/datetime/dateUtils'
import useOrderItemsEligibleForCarHireBundledDiscount from 'hooks/CarHire/useOrderItemsEligibleForCarHireBundledDiscount'
import { pluralizeToString } from 'lib/string/pluralize'
import Heading from 'components/Luxkit/Typography/Heading'
import LineCalendarIcon from 'components/Luxkit/Icons/line/LineCalendarIcon'
import BodyText from 'components/Luxkit/Typography/BodyText'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import TextLink from 'components/Luxkit/TextLink'
import TextButton from 'components/Luxkit/Button/TextButton'
import { pushWithRegion } from 'actions/NavigationActions'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { getDefaultUserDriverCategory } from 'selectors/carHireSelectors'
import { sortCarHireOffers } from '../carHireUtils'
import { take } from 'lib/array/arrayUtils'
import useCarHireList from 'hooks/CarHire/useCarHireList'
import qs from 'qs'
import CarHireUpsellOfferTile from '../CarHireOfferTile/CarHireUpsellOfferTile'
import FullWidthCarousel from 'components/Luxkit/Carousel/FullWidthCarousel'
import LayoutContainer from 'components/Common/LayoutContainer'
import CarHireUpsellOfferTileLoadingSkeleton from '../CarHireOfferTile/CarHireUpsellOfferTileLoadingSkeleton'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import { initialiseCheckout } from 'actions/CheckoutActions'
import { addToCart } from 'analytics/snowplow/events'
import { generateCarHireCheckoutItem } from '../CarHireCheckoutHelper'
import moment from 'moment'
import * as Analytics from 'analytics/analytics'
import StructureLabel from 'components/Luxkit/Label/StructureLabel'
import LabelGroup from 'components/Luxkit/Label/LabelGroup'

const MainContainer = styled(Group)`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(24)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr auto;
    column-gap: ${rem(24)};
    row-gap: ${rem(32)};
  }
`

const HotelContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr ${rem(68)};
  gap: ${rem(16)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: ${rem(164)} 1fr;
    gap: ${rem(24)};
  }
`

const HotelImageWrapper = styled.div`
  grid-column-start: 2;
  display: flex;
  justify-content: center;
  align-items: center;

  ${mediaQueryUp.tablet} {
    grid-column-start: 1;
  }
`

const HotelContentWrapper = styled(Group)`
  grid-column-start: 1;
  grid-row: 1;
  
  ${mediaQueryUp.tablet} {
    grid-column-start: 2;
  }
`

const DaysToGoLabel = styled(StructureLabel)`
  text-transform: uppercase;
`

const ViewAllButtonWrapper = styled(Group)`
  align-self: end;
  padding: ${rem(10)} ${rem(12)};

  ${mediaQueryUp.tablet} {
    padding: 0;
    grid-column-start: 2;
    grid-row: 1;
  }
`

interface Props {
  order: App.Order;
}

function CarHireDiscountUpcomingOrderCarousel(props: Props) {
  const { order } = props

  const { earliestItem, latestItem, place } = useOrderItemsEligibleForCarHireBundledDiscount(order.id)

  const daysToGo = useMemo(() => {
    if (!earliestItem) return 0
    return dateDifference(new Date(earliestItem.startDate), new Date()).days
  }, [earliestItem])

  const [defaultAgeCategory, defaultAge] = useAppSelector(getDefaultUserDriverCategory)
  const dispatch = useAppDispatch()
  const filters: App.CarHireOfferListFilters = useMemo(() => {
    return {
      pickUpLocationId: place?.id,
      dropOffLocationId: place?.id,
      pickUpDate: earliestItem?.startDate || undefined,
      dropOffDate: latestItem?.endDate || undefined,
      pickUpTime: '10:00',
      dropOffTime: '10:00',
      driversAgeCategory: defaultAgeCategory,
      isPickUpAtAirport: false,
      pickUpSearchType: 'geo',
      dropOffSearchType: 'geo',
      driversAge: defaultAge,
      bundledOrderId: order.id,
    }
  }, [earliestItem, latestItem, place?.id, defaultAge, defaultAgeCategory, order.id])

  const carHireList = useCarHireList(filters, 0)
  const offers = carHireList.vehicles
  const sortedOffers = useMemo(() => take(sortCarHireOffers(offers, 'price.asc'), 6), [offers])
  const pickUpName = place?.canonicalName

  const onSearch = useCallback(() => {
    const query = qs.stringify({
      pickUpName,
      dropOffName: pickUpName,
      pickUpId: filters.pickUpLocationId,
      dropOffId: filters.dropOffLocationId,
      pickUpDate: filters.pickUpDate,
      pickUpTime: '10:00',
      dropOffDate: filters.dropOffDate,
      dropOffTime: '10:00',
      ageCategory: filters.driversAgeCategory,
      age: filters.driversAge,
      isPickUpAtAirport: false,
      pickUpSearchType: 'geo',
      dropOffSearchType: 'geo',
      bundledOrderId: order.id,
    })

    dispatch(pushWithRegion('/search/car-hire', query))
  }, [filters, pickUpName, dispatch, order.id])

  const onGoToCheckout = useCallback((offer: App.CarHireOffer) => {
    if (filters) {
      const [pickUpDate, pickUpTime] = moment.utc(carHireList.pickUp.date).format('YYYY-MM-DD|HH:mm').split('|')
      const [dropOffDate, dropOffTime] = moment.utc(carHireList.dropOff.date).format('YYYY-MM-DD|HH:mm').split('|')

      const item = generateCarHireCheckoutItem(offer, {
        dropOffDate,
        dropOffTime,
        dropOffLocation: pickUpName,
        dropOffLocationId: filters.pickUpLocationId || '',
        pickUpLocation: pickUpName,
        pickUpLocationId: filters.pickUpLocationId || '',
        pickUpDate,
        pickUpTime,
        ageCategory: filters.driversAgeCategory || 2,
        age: filters.driversAge || 25,
        isPickUpAtAirport: !!filters.isPickUpAtAirport,
        resultsTimestamp: carHireList.timestamp || 0,
      })

      dispatch(initialiseCheckout([item]))
      Analytics.trackEvent(addToCart())
      dispatch(navigateToCheckout())
    }
  }, [dispatch, filters, pickUpName, carHireList])

  const onManageBooking = useCallback(() => {
    dispatch(pushWithRegion(`/account/my-escapes/order/${order.id}`))
  }, [dispatch, order.id])

  return <>
    <LayoutContainer>
      <MainContainer direction="vertical" gap={20} desktopGap={32}>
        {!!earliestItem && !!latestItem && <HotelContainer>
          <HotelImageWrapper>
            <CSSBreakpoint min="desktop">
              <Image id={earliestItem.offer.image.id} width={164} height={88} />
            </CSSBreakpoint>
            <CSSBreakpoint max="tablet">
              <Image id={earliestItem.offer.image.id} width={68} height={68} />
            </CSSBreakpoint>
          </HotelImageWrapper>
          <HotelContentWrapper direction="vertical" gap={4} desktopGap={8}>
            <LabelGroup>
              <DaysToGoLabel variant="promotion">
                {pluralizeToString('day', daysToGo)} to go
              </DaysToGoLabel>
            </LabelGroup>
            <Heading variant="heading3">
              {earliestItem.offer.name}
            </Heading>
            <Group direction="horizontal" gap={8} verticalAlign="center">
              <LineCalendarIcon size="S" />
              <BodyText variant="medium">
                <FormatDateTime value={earliestItem.startDate} format="day-month-name" />{' - '}
                <FormatDateTime value={latestItem.endDate} format="day-month-name" />
              </BodyText>
              <BodyText variant="medium">·</BodyText>
              <TextLink size="medium" onClick={onManageBooking}>
                Manage booking
              </TextLink>
            </Group>
          </HotelContentWrapper>
        </HotelContainer>}
        <CSSBreakpoint min="desktop">
          <ViewAllButtonWrapper direction="horizontal" horizontalAlign="end" verticalAlign="baseline">
            <TextButton kind="tertiary" size="medium" onClick={onSearch}>
              View all options
            </TextButton>
          </ViewAllButtonWrapper>
        </CSSBreakpoint>
      </MainContainer>
    </LayoutContainer>
    <FullWidthCarousel>
      {carHireList.fetching && <>
        <CarHireUpsellOfferTileLoadingSkeleton />
        <CarHireUpsellOfferTileLoadingSkeleton />
        <CarHireUpsellOfferTileLoadingSkeleton />
        <CarHireUpsellOfferTileLoadingSkeleton />
      </>}
      {!carHireList.fetching && sortedOffers.map(offer => (
        <CarHireUpsellOfferTile key={offer.id} offer={offer} onOfferClick={onGoToCheckout} />
      ))}
    </FullWidthCarousel>
    <CSSBreakpoint max="tablet">
      <ViewAllButtonWrapper direction="horizontal" horizontalAlign="end" verticalAlign="baseline">
        <TextButton kind="tertiary" size="medium" onClick={onSearch}>
          View all options
        </TextButton>
      </ViewAllButtonWrapper>
    </CSSBreakpoint>
  </>
}

export default CarHireDiscountUpcomingOrderCarousel
